/**
 * "Your Collections" tab. With no collection open:
 *   - the brand filter (All brands + one pill per brand; defaults to All brands) — it drives everything below;
 *   - "Your collections" on top: the team's collections (2×2 cover mosaic, brand logo, "N sounds · updated 2d ago";
 *     the play button plays the collection as a queue) and "+ New collection" last;
 *   - then "All sounds" as a listing (search, Everyone / Only me, source, Play / Shuffle and the track table).
 * A card opens the collection page (CollectionPage). The selection lives in the page URL (`&collection=<id>`,
 * `&collection=all` for the full-page All sounds); the page owns it and passes it down.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/components/ui/utils';
import { type CollectionSummary, type StudioTrack } from '@/api/audioStudioApi';
import { usePlayer } from './AudioPlayer';
import { BrandAvatar, BrandPills, useStudioBrands } from './brands';
import { CollectionArtwork, NeonPlayButton } from './CollectionArt';
import { AllSoundsPage, CollectionPage } from './CollectionPage';
import { type SoundsScope } from './SoundRows';
import { collectionQueueKey } from './collectionKeys';
import { COLLECTION_NAME_MAX, collectionErrorMessage, useCreateCollection } from './collectionMutations';
import { formatAgo, soundCount, studioPlayable, useNow } from './format';
import { collectionQueryOptions, useCollections } from './queries';

export function CollectionsTab({ selected, onSelect, allScope, onCreateSound: _onCreateSound }: {
  /** null = the home; `ALL_SOUNDS` = All sounds; else a collection id. */
  selected: string | null;
  onSelect: (id: string | null) => void;
  /** Everyone / Only me for All sounds (Recent creations → See all passes its Team / Mine). */
  allScope: SoundsScope;
  /** "Make a sound" from the empty states: the Studio tab's prompt. */
  onCreateSound?: () => void;
}) {
  // Default is "All brands": sounds made before brand tagging (or without a brand) must never look missing.
  // Picking a pill filters; the banner chip only sets which brand NEW sounds/collections get.
  const [brandFilter, setBrandFilter] = useState<string | null>(null);

  if (selected) {
    return (
      <CollectionPage
        key={selected}
        id={selected}
        brandFilter={brandFilter}
        onBrandFilter={setBrandFilter}
        allScope={allScope}
        onBack={() => onSelect(null)}
      />
    );
  }
  return (
    <section aria-label="Your Collections" className="space-y-9">
      <BrandPills value={brandFilter} onChange={setBrandFilter} />
      {/* Collections on top, then every sound as a listing (the "All sounds" table). */}
      <CollectionsGrid brandId={brandFilter} onSelect={onSelect} />
      <AllSoundsPage embedded brandFilter={brandFilter} onBrandFilter={setBrandFilter} initialScope={allScope} />
    </section>
  );
}

const sectionTitle = 'text-[20px] font-bold leading-7 tracking-[-0.01em] text-[#101828]';

// ─── Collections grid ───────────────────────────────────────────────────────

/** Plays a list as a queue — or pauses / resumes it when it is the one already playing. */
function usePlayList() {
  const qc = useQueryClient();
  const { queueKey, isPlaying, pauseResume, playQueue } = usePlayer();
  const [loading, setLoading] = useState<string | null>(null);
  const play = async (key: string, load: () => Promise<StudioTrack[]>) => {
    if (queueKey === key) { pauseResume(); return; }
    setLoading(key);
    try {
      const tracks = (await load()).filter((t) => t.audio_url);
      if (!tracks.length) toast('No sounds to play here yet');
      else playQueue(tracks.map(studioPlayable), 0, { key });
    } catch (e) {
      toast.error((e as Error).message || 'Could not load the sounds');
    } finally {
      setLoading(null);
    }
  };
  return { qc, play, loading, playingKey: isPlaying ? queueKey : null };
}

const cardCls = 'group/card relative flex min-w-0 flex-col rounded-[16px] p-3 transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#f9fafb] focus-within:bg-[#f9fafb]';
const artCls = 'relative aspect-square overflow-hidden rounded-[12px] shadow-[0_1px_3px_rgba(16,24,40,0.08)]';
/** The card's name is a button stretched over the whole card; the play button sits above it. */
const openCls = 'truncate text-left text-[14.5px] leading-5 text-[#101828] outline-none after:absolute after:inset-0 after:rounded-[16px] after:content-[""] focus-visible:after:ring-2 focus-visible:after:ring-[#be43ff]/40';
const cardPlayCls = (on: boolean) => cn(
  'absolute bottom-2 right-2 z-10 transition-all',
  on ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-0 focus-visible:translate-y-0 focus-visible:opacity-100 group-hover/card:translate-y-0 group-hover/card:opacity-100 [@media(hover:none)]:translate-y-0 [@media(hover:none)]:opacity-100',
);

function CollectionsGrid({ brandId, onSelect }: { brandId: string | null; onSelect: (id: string | null) => void }) {
  const collections = useCollections(true, brandId);
  const now = useNow();
  const { qc, play, loading, playingKey } = usePlayList();
  const list = collections.data ?? [];

  return (
    <section aria-labelledby="audio-shelf-collections">
      <h2 id="audio-shelf-collections" className={cn(sectionTitle, 'mb-2')}>Your collections</h2>
      <ul aria-label="Collections" className="-mx-3 grid grid-cols-2 gap-1 sm:grid-cols-[repeat(auto-fill,minmax(170px,1fr))] sm:gap-5">
        {list.map((c) => (
          <CollectionCard
            key={c.collection_id}
            collection={c}
            now={now}
            playing={playingKey === collectionQueueKey(c.collection_id)}
            loading={loading === collectionQueueKey(c.collection_id)}
            onOpen={() => onSelect(c.collection_id)}
            onPlay={() => void play(collectionQueueKey(c.collection_id), async () => (await qc.fetchQuery(collectionQueryOptions(c.collection_id))).tracks)}
          />
        ))}

        <NewCollectionCard brandId={brandId} onCreated={(id) => onSelect(id)} />
      </ul>
      {collections.isLoading && <p className="mt-2 flex items-center gap-2 text-[12.5px] text-[#667085]" role="status"><Loader2 className="h-4 w-4 animate-spin text-[#98a2b3]" aria-hidden /> Loading collections…</p>}
      {collections.isError && <p className="mt-2 text-[12.5px] text-[#b42318]" role="alert">Could not load collections: {(collections.error as Error).message}</p>}
      {collections.isSuccess && list.length === 0 && (
        <p className="mt-2 text-[13px] text-[#667085]">Group sounds for a campaign, a launch or a brand — start your first collection.</p>
      )}
    </section>
  );
}

function CollectionCard({ collection: c, now, playing, loading, onOpen, onPlay }: {
  collection: CollectionSummary;
  now: number;
  playing: boolean;
  loading: boolean;
  onOpen: () => void;
  onPlay: () => void;
}) {
  const brand = useStudioBrands().brandOf(c.brand_id);
  const meta = [brand?.brandName, soundCount(c.track_count), c.updated_at ? `updated ${formatAgo(c.updated_at, now)}` : null].filter(Boolean).join(' · ');
  return (
    <li className={cardCls} data-collection-id={c.collection_id}>
      <div className={artCls}>
        <CollectionArtwork name={c.name} covers={c.cover_urls} />
        {brand && <span className="absolute bottom-2 left-2 rounded-[6px] bg-white p-0.5 shadow-sm" title={brand.brandName}><BrandAvatar brand={brand} size={20} /></span>}
        <NeonPlayButton playing={playing} label={playing ? `Pause ${c.name}` : `Play ${c.name}`} onClick={onPlay} disabled={loading || c.track_count === 0} className={cardPlayCls(playing)} />
      </div>
      <button type="button" onClick={onOpen} className={cn(openCls, 'mt-3')} style={{ fontWeight: 600 }}>{c.name}</button>
      <p className="mt-0.5 truncate text-[12.5px] leading-[18px] text-[#667085]" title={meta}>{meta}</p>
    </li>
  );
}

function NewCollectionCard({ brandId, onCreated }: { brandId: string | null; onCreated: (id: string) => void }) {
  const create = useCreateCollection();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const close = () => { setOpen(false); setName(''); setError(null); };
  const submit = () => {
    const n = name.trim();
    if (!n) return;
    create.mutate({ name: n, ...(brandId ? { brand_id: brandId } : {}) }, {
      onSuccess: (c) => { toast.success(`Created “${c.name}”`); close(); onCreated(c.collection_id); },
      onError: (e) => setError(collectionErrorMessage(e)),
    });
  };

  return (
    <li className="flex min-w-0 flex-col p-3">
      {open ? (
        <form
          aria-label="New collection"
          onSubmit={(e) => { e.preventDefault(); submit(); }}
          className="flex aspect-square flex-col justify-center gap-2 rounded-[12px] border border-dashed border-[#be43ff]/50 bg-[#fcfaff] p-3"
        >
          <input
            autoFocus
            value={name}
            onChange={(e) => { setName(e.target.value); setError(null); }}
            onKeyDown={(e) => { if (e.key === 'Escape') close(); }}
            maxLength={COLLECTION_NAME_MAX}
            placeholder="Collection name"
            aria-label="New collection name"
            aria-invalid={Boolean(error)}
            className="h-9 w-full min-w-0 rounded-[8px] border border-[#d0d5dd] bg-white px-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#be43ff]/20"
          />
          {error && <p className="text-[12px] leading-4 text-[#b42318]" role="alert">{error}</p>}
          <div className="flex items-center gap-1.5">
            <button type="submit" disabled={!name.trim() || create.isPending} className="inline-flex h-8 flex-1 items-center justify-center gap-1 rounded-full bg-[#2e024c] px-3 text-[12.5px] text-white hover:bg-[#45066f] disabled:opacity-50" style={{ fontWeight: 600 }}>
              {create.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />} Create
            </button>
            <button type="button" onClick={close} className="h-8 rounded-full px-2.5 text-[12.5px] text-[#667085] hover:bg-white">Cancel</button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex aspect-square flex-col items-center justify-center gap-2 rounded-[12px] border border-dashed border-[#d0d5dd] text-[#667085] outline-none transition-colors hover:border-[#be43ff]/60 hover:bg-[#fcfaff] hover:text-[#6941c6] focus-visible:ring-2 focus-visible:ring-[#be43ff]/30"
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#f2f4f7]"><Plus className="h-5 w-5" aria-hidden /></span>
          <span className="text-[13.5px]" style={{ fontWeight: 600 }}>New collection</span>
        </button>
      )}
    </li>
  );
}
